import { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { getBackendUrl } from '../config/api';

const ChatContext = createContext(null);

export function ChatProvider({ children }) {
  const { token, user } = useAuth();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activePartnerId, setActivePartnerId] = useState(null);
  const [listingContext, setListingContext] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const wsRef = useRef(null);
  const pollRef = useRef(null);

  // Fetch unread count
  const fetchUnreadCount = useCallback(async () => {
    if (!token) return;
    try {
      const resp = await fetch(`${getBackendUrl()}/api/messages/unread/count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (resp.ok) {
        const data = await resp.json();
        setUnreadCount(data.unread_count);
      }
    } catch (e) { /* silent */ }
  }, [token]);

  // Poll unread count every 15 seconds
  useEffect(() => {
    if (!token) return;
    fetchUnreadCount();
    pollRef.current = setInterval(fetchUnreadCount, 15000);
    return () => clearInterval(pollRef.current);
  }, [token, fetchUnreadCount]);

  // WebSocket connection for real-time updates
  useEffect(() => {
    if (!token || !user?.id) return;

    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${wsProtocol}//${window.location.host}/api/ws/chat/${user.id}?token=${token}`;

    let ws;
    let reconnectTimeout;

    const connect = () => {
      try {
        ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (data.type === 'new_message') {
              setUnreadCount(c => c + 1);
              // Dispatch custom event for open chat windows
              window.dispatchEvent(new CustomEvent('chat-new-message', { detail: data.message }));
            }
          } catch (e) { /* ignore parse errors */ }
        };

        ws.onclose = () => {
          wsRef.current = null;
          reconnectTimeout = setTimeout(connect, 5000);
        };

        ws.onerror = () => {
          ws.close();
        };

        // Keepalive ping every 30s
        const pingInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, 30000);

        ws.onopen = () => {};

        // Store cleanup for ping
        ws._pingInterval = pingInterval;
      } catch (e) {
        reconnectTimeout = setTimeout(connect, 5000);
      }
    };

    connect();

    return () => {
      clearTimeout(reconnectTimeout);
      if (wsRef.current) {
        clearInterval(wsRef.current._pingInterval);
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [token, user?.id]);

  // Open drawer to a specific conversation
  const openChat = useCallback((partnerId, listing = null) => {
    setActivePartnerId(partnerId);
    setListingContext(listing);
    setDrawerOpen(true);
  }, []);

  // Open drawer to conversations list
  const openDrawer = useCallback(() => {
    setActivePartnerId(null);
    setListingContext(null);
    setDrawerOpen(true);
  }, []);

  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
  }, []);

  // Send typing indicator via WebSocket
  const sendTyping = useCallback((partnerId) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'typing', partner_id: partnerId }));
    }
  }, []);

  return (
    <ChatContext.Provider value={{
      drawerOpen, openDrawer, closeDrawer, openChat,
      activePartnerId, setActivePartnerId,
      listingContext, setListingContext,
      unreadCount, setUnreadCount, fetchUnreadCount,
      sendTyping,
    }}>
      {children}
    </ChatContext.Provider>
  );
}

export const useChat = () => {
  const ctx = useContext(ChatContext);
  if (!ctx) return { unreadCount: 0, openDrawer: () => {}, closeDrawer: () => {}, openChat: () => {}, drawerOpen: false };
  return ctx;
};
